import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { PlusCircle, LayoutDashboard, LogOut, User, Sparkles, ShieldCheck, Users, Handshake } from "lucide-react";

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (!user) return null;

  const links = [
    { to: "/post", label: "Request an Errand", id: "request", icon: PlusCircle },
    { to: "/services", label: "Explore Services", id: "services", icon: Handshake },
    { to: "/pods", label: "Join a Pod", id: "pods", icon: Users },
    { to: "/dashboard", label: "Dashboard", id: "dashboard", icon: LayoutDashboard },
  ];
  if (user.role === "admin") links.push({ to: "/admin", label: "Admin", id: "admin", icon: ShieldCheck });

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      {/* Desktop / top */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-gray-200/60">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/feed" data-testid="nav-logo" className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-2xl bg-primary flex items-center justify-center text-white">
              <Sparkles size={18} />
            </span>
            <span className="font-heading font-extrabold text-xl tracking-tight text-[#1A1D20]">Errandell</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.id}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                  pathname === l.to ? "bg-primary/10 text-primary" : "text-[#4A5361] hover:bg-muted"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to={`/profile/${user.id}`} data-testid="nav-profile" className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-muted transition-colors">
              <span className="w-8 h-8 rounded-full bg-secondary/20 text-secondary flex items-center justify-center font-bold text-sm">
                {user.display_name?.[0]?.toUpperCase()}
              </span>
              <span className="hidden sm:block text-sm font-semibold text-[#1A1D20]">{user.display_name}</span>
            </Link>
            <Button data-testid="nav-logout-btn" aria-label="Log out" variant="ghost" size="icon" onClick={doLogout} className="rounded-full text-[#6B7280]">
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl bg-white/85 border-t border-gray-200/60 px-6 py-2 flex justify-between">
        {links.map((l) => {
          const Icon = l.icon;
          const active = pathname === l.to;
          return (
            <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.id}`}
              className={`flex flex-col items-center gap-1 px-3 py-1 rounded-xl ${active ? "text-primary" : "text-[#6B7280]"}`}>
              <Icon size={22} />
              <span className="text-[10px] font-semibold">{l.label}</span>
            </Link>
          );
        })}
        <Link to={`/profile/${user.id}`} className="flex flex-col items-center gap-1 px-3 py-1 rounded-xl text-[#6B7280]">
          <User size={22} />
          <span className="text-[10px] font-semibold">Profile</span>
        </Link>
      </nav>
    </>
  );
}
